/**
 * Port of Houston - Combined Capabilities Statement & Demo
 * Janitorial + Water Purification + Medical/First Responder + AskIsaac PM
 */
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, ArrowLeft, Play, CheckCircle2,
  Shield, Clock, BarChart3, Phone, Mail, Droplets, Sparkles,
  Building2, Target, Zap, Award, DollarSign, Users, Wrench,
  ShoppingCart, Layers, Settings, Anchor, Package, Truck,
  Heart, AlertTriangle, Stethoscope, Activity, ClipboardCheck,
  FileCheck, Eye, Gauge, Calculator, LayoutDashboard, Lock,
  Globe, RefreshCw, Cpu, Brain, Workflow, Briefcase, HardHat,
  Pipette, Flame, Thermometer, CircleDollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

const PORT_HERO = "https://images.pexels.com/photos/36652843/pexels-photo-36652843.jpeg?auto=compress&cs=tinysrgb&w=1200";
const HOUSTON_SKYLINE = "https://images.unsplash.com/photo-1639768843839-fd3a2935e64d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxQb3J0JTIwb2YlMjBIb3VzdG9uJTIwaW5kdXN0cmlhbCUyMGZhY2lsaXR5fGVufDB8fHx8MTc3NDUzNDQ5NHww&ixlib=rb-4.1.0&q=85";
const INDUSTRIAL_FACILITY = "https://images.pexels.com/photos/36230779/pexels-photo-36230779.jpeg?auto=compress&cs=tinysrgb&w=800";

// Filmop products
const FILMOP_CART = "https://www.filmopusa.com/images/product-page-alpha.gif";
const FILMOP_EQUODOSE = "https://www.filmopusa.com/images/product-page-alpha-healthcare-equodose.gif";
const FILMOP_BUCKETS = "https://www.filmopusa.com/images/product-page-dual-bucket-spin-n-drop.gif";

// Water purification images
const WATER_SOFTENER_IMG = "/images/water_softener_sterilemate.png";
const WATER_LAB_IMG = "https://images.pexels.com/photos/7298554/pexels-photo-7298554.jpeg?auto=compress&cs=tinysrgb&w=800";
const WATER_RO_IMG = "https://images.pexels.com/photos/5712211/pexels-photo-5712211.jpeg?auto=compress&cs=tinysrgb&w=800";

// Medical / First Responder images
const AED_IMG = "https://images.unsplash.com/photo-1617257484989-9f8ab411c362?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxBRUQlMjBkZWZpYnJpbGxhdG9yJTIwZW1lcmdlbmN5JTIwbWVkaWNhbCUyMGZpcnN0JTIwcmVzcG9uZGVyfGVufDB8fHx8MTc3NDUzNjE4M3ww&ixlib=rb-4.1.0&q=85";
const FIRST_AID_IMG = "https://images.pexels.com/photos/5673523/pexels-photo-5673523.jpeg?auto=compress&cs=tinysrgb&w=800";
const EMT_IMG = "https://images.pexels.com/photos/6520071/pexels-photo-6520071.jpeg?auto=compress&cs=tinysrgb&w=800";

// Who We Are slide images
const HOUSTON_SUNSET = "https://images.pexels.com/photos/2442023/pexels-photo-2442023.jpeg?auto=compress&cs=tinysrgb&w=1200";
const MEDICAL_SUPPLIES_IMG = "https://images.pexels.com/photos/6519900/pexels-photo-6519900.jpeg?auto=compress&cs=tinysrgb&w=800";

// Slide navigation
function SlideNav({ current, total, onPrev, onNext }) {
  return (
    <div className="flex items-center justify-between mt-8">
      <Button variant="outline" onClick={onPrev} disabled={current === 0} className="border-slate-600 text-slate-300 hover:bg-slate-800">
        <ChevronLeft className="w-5 h-5 mr-1" /> Previous
      </Button>
      <div className="flex items-center gap-2">
        {Array.from({ length: total }, (_, i) => (
          <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all ${i === current ? "bg-cyan-400 scale-125" : "bg-slate-600"}`} />
        ))}
      </div>
      <Button variant="outline" onClick={onNext} disabled={current === total - 1} className="border-slate-600 text-slate-300 hover:bg-slate-800">
        Next <ChevronRight className="w-5 h-5 ml-1" />
      </Button>
    </div>
  );
}

function StatBox({ value, label, icon: Icon, color = "cyan" }) {
  const colors = { cyan: "bg-cyan-500/20 text-cyan-400", emerald: "bg-emerald-500/20 text-emerald-400", amber: "bg-amber-500/20 text-amber-400", red: "bg-red-500/20 text-red-400", violet: "bg-violet-500/20 text-violet-400", blue: "bg-blue-500/20 text-blue-400" };
  return (
    <div className="bg-slate-800/60 rounded-xl p-5 border border-slate-700/50 text-center">
      <div className={`w-12 h-12 ${colors[color]} rounded-lg flex items-center justify-center mx-auto mb-3`}>
        <Icon className="w-6 h-6" />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-slate-400 text-sm mt-1">{label}</p>
    </div>
  );
}

function FeatureRow({ icon: Icon, title, desc, color = "text-cyan-400" }) {
  return (
    <div className="flex items-start gap-4 p-4 bg-slate-800/40 rounded-xl border border-slate-700/30">
      <div className={`w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <h4 className="text-white font-semibold text-sm">{title}</h4>
        <p className="text-slate-400 text-sm mt-0.5">{desc}</p>
      </div>
    </div>
  );
}

export default function PortOfHoustonDemo() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef(null);

  const slides = [
    // SLIDE 0: Title / Hero
    () => (
      <div className="relative min-h-[600px] flex items-center" data-testid="slide-hero">
        <img src={PORT_HERO} alt="Port of Houston" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent" />
        <div className="relative z-10 max-w-3xl px-8">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2 mb-6">
            <Anchor className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-300 text-sm font-medium">Capabilities Statement</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Port of Houston<br />
            <span className="text-cyan-400">Comprehensive Services</span>
          </h1>
          <p className="text-xl text-slate-300 mb-3">
            Infrastructure, Intelligence & Emergency Readiness
          </p>
          <p className="text-slate-400 max-w-2xl">
            Sterile Mate LLC delivers a unified service model combining industrial janitorial operations, water purification systems, medical/first responder supply, and AI-powered project management — purpose-built for large-scale port and maritime facilities.
          </p>
          <div className="flex items-center gap-4 mt-8">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-10" />
            <div className="border-l border-slate-600 pl-4">
              <p className="text-white font-semibold">Sterile Mate LLC</p>
              <p className="text-slate-400 text-sm">Powered by AskIsaac Intelligence</p>
            </div>
          </div>
        </div>
      </div>
    ),

    // SLIDE 1: Company Overview — Origin Story & Credentials
    () => (
      <div className="p-8" data-testid="slide-overview">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Who We Are</h2>
            <p className="text-slate-400 text-sm">Sterile Mate LLC — Houston, TX | Est. 2020</p>
          </div>
        </div>

        {/* Hero Banner: Origin Story */}
        <div className="relative rounded-xl overflow-hidden mb-6 h-52">
          <img src={HOUSTON_SUNSET} alt="Houston Skyline" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-transparent flex items-center">
            <div className="p-6 max-w-2xl">
              <p className="text-cyan-400 text-sm font-semibold mb-2">OUR ORIGIN STORY</p>
              <p className="text-white text-lg font-bold leading-snug">
                Born in the Pandemic. Built for Houston&apos;s Hospitals.
              </p>
              <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                When COVID-19 overwhelmed Houston&apos;s healthcare system, founder <span className="text-white font-semibold">George Broughton</span> stepped
                in as a frontline triage supplier — sourcing and delivering critical PPE, medical devices, and infection prevention supplies
                directly to hospitals in crisis. What began as an emergency response became a mission: build a company that healthcare
                facilities can depend on — not just in a crisis, but every day.
              </p>
            </div>
          </div>
        </div>

        {/* Key Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <StatBox value="2020" label="Founded During COVID-19" icon={Shield} color="cyan" />
          <StatBox value="$1.1M" label="2024 Revenue" icon={DollarSign} color="emerald" />
          <StatBox value="HUB" label="Certified | SAM.gov" icon={Award} color="amber" />
          <StatBox value="Licensed" label="Medical Device Supplier" icon={Stethoscope} color="red" />
        </div>

        {/* Two Column: Story + Credentials */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Left: The Vision */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-cyan-500/20">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Target className="w-5 h-5 text-cyan-400" />
              From Crisis Response to Full-Service Partner
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              George saw firsthand how fragmented the hospital supply chain was — vendors that couldn&apos;t deliver, systems that didn&apos;t
              communicate, and compliance gaps that put patients at risk. He built Sterile Mate to solve all of it under one roof.
            </p>
            <p className="text-slate-300 text-sm leading-relaxed">
              Today, Sterile Mate is a <span className="text-white font-semibold">fully licensed medical device supplier</span> and
              infrastructure services company serving healthcare systems, government agencies, and industrial facilities across Texas.
              Every service line — from janitorial to water purification to first responder supply — is powered by our
              proprietary <span className="text-cyan-400 font-semibold">AskIsaac.io</span> technology platform.
            </p>
          </div>

          {/* Right: Credentials & Certifications */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-amber-500/20">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-amber-400" />
              Credentials & Certifications
            </h3>
            <div className="space-y-3">
              {[
                { label: "Licensed Medical Device Distributor", desc: "FDA-regulated Class I, II, and III device supply chain", color: "text-red-400" },
                { label: "HUB Certified", desc: "State of Texas Historically Underutilized Business", color: "text-amber-400" },
                { label: "Government Contractor", desc: "Active contracts with VA, SAM.gov, DoD, Harris County, State of Texas", color: "text-emerald-400" },
                { label: "AskIsaac.io Technology Platform", desc: "Proprietary AI-powered project management, compliance & procurement", color: "text-cyan-400" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className={`w-4 h-4 ${item.color} mt-0.5 flex-shrink-0`} />
                  <div>
                    <p className="text-white text-sm font-medium">{item.label}</p>
                    <p className="text-slate-400 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Revenue Growth */}
            <div className="mt-4 pt-4 border-t border-slate-700/50">
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Revenue Growth</p>
              <div className="flex items-end gap-3">
                {[
                  { year: "2021", val: "$800K", h: "h-8" },
                  { year: "2022", val: "$700K", h: "h-7" },
                  { year: "2023", val: "$600K", h: "h-6" },
                  { year: "2024", val: "$1.1M", h: "h-12" },
                ].map(item => (
                  <div key={item.year} className="flex-1 text-center">
                    <div className={`${item.h} bg-cyan-500/30 rounded-t-sm mx-auto w-full max-w-[40px] border-t-2 border-cyan-400`} />
                    <p className="text-white text-xs font-semibold mt-1">{item.val}</p>
                    <p className="text-slate-500 text-[10px]">{item.year}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 5D Service Engine */}
        <div className="bg-gradient-to-r from-cyan-900/30 to-slate-800/50 rounded-xl p-6 border border-cyan-500/20">
          <h3 className="text-white font-semibold mb-3">Our 5D Service Engine</h3>
          <div className="grid grid-cols-5 gap-3">
            {[
              { d: "D1", name: "Distribution", desc: "Supply chain & logistics" },
              { d: "D2", name: "Decontamination", desc: "EVS & infection prevention" },
              { d: "D3", name: "Dialysis/Water", desc: "Purification systems" },
              { d: "D4", name: "DME Supply", desc: "Medical devices & first aid" },
              { d: "D5", name: "Digital", desc: "AskIsaac AI platform" },
            ].map(item => (
              <div key={item.d} className="text-center p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                <p className="text-cyan-400 font-bold text-lg">{item.d}</p>
                <p className="text-white text-xs font-semibold mt-1">{item.name}</p>
                <p className="text-slate-500 text-xs mt-0.5">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),

    // SLIDE 2: Janitorial & EVS
    () => (
      <div className="p-8" data-testid="slide-janitorial">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Industrial Janitorial & EVS</h2>
            <p className="text-slate-400 text-sm">Filmop USA + CleanRead ATP Technology</p>
          </div>
        </div>
        <p className="text-slate-300 mb-6 max-w-3xl">
          Port-grade cleaning systems designed for high-traffic terminals, warehouses, offices, and marine facilities.
          Our Filmop USA partnership brings European-engineered cleaning technology purpose-built for demanding industrial environments.
        </p>
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/30 overflow-hidden">
            <img src={FILMOP_CART} alt="Alpha Workstation" className="w-full h-40 object-contain bg-white p-2" />
            <div className="p-4">
              <h4 className="text-white font-semibold text-sm">Alpha Workstation</h4>
              <p className="text-slate-400 text-xs mt-1">Modular cart system for port facility cleaning — configurable for terminals, offices, and industrial areas</p>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/30 overflow-hidden">
            <img src={FILMOP_EQUODOSE} alt="Equo-Dose System" className="w-full h-40 object-contain bg-white p-2" />
            <div className="p-4">
              <h4 className="text-white font-semibold text-sm">Equo-Dose Chemical System</h4>
              <p className="text-slate-400 text-xs mt-1">Precise chemical dilution for consistent, cost-effective cleaning across large port facilities</p>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/30 overflow-hidden">
            <img src={FILMOP_BUCKETS} alt="Dual Bucket System" className="w-full h-40 object-contain bg-white p-2" />
            <div className="p-4">
              <h4 className="text-white font-semibold text-sm">Spin-N-Drop Wringing</h4>
              <p className="text-slate-400 text-xs mt-1">Hands-free wringing prevents cross-contamination — critical for port sanitation compliance</p>
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <FeatureRow icon={Shield} title="CleanRead ATP Verification" desc="Bioluminescence testing validates cleanliness at the molecular level. Audit-ready results." color="text-emerald-400" />
          <FeatureRow icon={Target} title="Zone-Based Cleaning Plans" desc="Custom protocols per area type: office, terminal, warehouse, dockside, mechanical." color="text-emerald-400" />
          <FeatureRow icon={DollarSign} title="Cost Reduction" desc="Microfiber system reduces chemical use by up to 90% and water use by up to 95%." color="text-emerald-400" />
          <FeatureRow icon={Award} title="Sustainability" desc="Washable, reusable textiles reduce waste. Closed-loop chemical management." color="text-emerald-400" />
        </div>
      </div>
    ),

    // SLIDE 3: Water Purification - Enhanced
    () => (
      <div className="p-8" data-testid="slide-water">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
            <Droplets className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Water Purification & Treatment Systems</h2>
            <p className="text-slate-400 text-sm">TRITTON IXC + Ameriwater + AAMI ST108 Compliance</p>
          </div>
        </div>

        {/* Hero Image Banner */}
        <div className="relative rounded-xl overflow-hidden mb-6 h-48">
          <img src={WATER_SOFTENER_IMG} alt="Sterile Mate Water Softener Systems" className="absolute right-0 top-0 h-full w-48 object-contain bg-slate-800/80 p-2" />
          <img src={WATER_LAB_IMG} alt="Water Quality Lab" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/70 to-transparent flex items-center">
            <div className="p-6 max-w-xl">
              <p className="text-blue-400 text-sm font-semibold mb-1">PROVEN TRACK RECORD</p>
              <p className="text-white text-lg font-bold">Active Contracts with Harris County Health System & the State of Texas</p>
              <p className="text-slate-300 text-sm mt-1">Serving Reverse Osmosis systems and Water Softener installations across multiple Harris Health facilities and Texas state-contracted locations.</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <StatBox value="RO/DI" label="Reverse Osmosis & DI" icon={Droplets} color="blue" />
          <StatBox value="Harris" label="County Health System" icon={Building2} color="emerald" />
          <StatBox value="Texas" label="State Contracts" icon={Award} color="amber" />
          <StatBox value="AAMI" label="ST108 Compliant" icon={FileCheck} color="cyan" />
        </div>

        {/* Three Core Programs */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-slate-800/50 rounded-xl border border-blue-500/20 overflow-hidden">
            <div className="bg-blue-500/10 p-4 border-b border-blue-500/20">
              <h3 className="text-blue-400 font-bold flex items-center gap-2">
                <Droplets className="w-5 h-5" />
                Facility-Wide Water Softening
              </h3>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-slate-300 text-sm">Complete water softener systems for entire facilities — boilers, cooling towers, sterilizers, laundry, ice machines, and domestic hot water.</p>
              <ul className="space-y-1.5 text-sm text-slate-400 mt-3">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />Duplex & simplex softener configurations</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />Hardness reduction to &lt;1 GPG</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />Salt delivery & brine management</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />Protects equipment & extends lifespan</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />24/7 remote monitoring & alerts</li>
              </ul>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl border border-cyan-500/20 overflow-hidden">
            <div className="bg-cyan-500/10 p-4 border-b border-cyan-500/20">
              <h3 className="text-cyan-400 font-bold flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                DI Tank Exchange Program
              </h3>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-slate-300 text-sm">Zero-downtime deionized water supply through our managed tank exchange service. Fresh tanks delivered, spent tanks regenerated off-site.</p>
              <ul className="space-y-1.5 text-sm text-slate-400 mt-3">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />Scheduled exchange cycles</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />18 megohm ultra-pure DI water</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />No capital equipment cost</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />Lab, SPD, & pharmacy-grade purity</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />Emergency rapid-exchange available</li>
              </ul>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl border border-emerald-500/20 overflow-hidden">
            <div className="bg-emerald-500/10 p-4 border-b border-emerald-500/20">
              <h3 className="text-emerald-400 font-bold flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Reverse Osmosis Systems
              </h3>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-slate-300 text-sm">Commercial and industrial RO systems for critical applications — dialysis, laboratory, sterile processing, and central plant operations.</p>
              <ul className="space-y-1.5 text-sm text-slate-400 mt-3">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />TRITTON IXC & Ameriwater systems</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />99.9% contaminant rejection</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />Active Harris Health RO contracts</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />State of Texas procurement approved</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />Full AAMI ST108 compliance</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Port-Specific Applications & Turnkey Model */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-slate-800/50 rounded-xl p-6 border border-blue-500/20">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Pipette className="w-5 h-5 text-blue-400" />
              Port-Specific Applications
            </h3>
            <div className="space-y-3">
              {[
                "Facility-wide softening for boilers, cooling towers & domestic water",
                "DI tank exchange for laboratory and quality-control testing",
                "RO systems for on-site medical clinics & first aid stations",
                "Potable water supply for terminal buildings & crew facilities",
                "Ice machine & food service water filtration",
                "Wastewater pre-treatment for dock runoff",
                "Emergency water purification for disaster response"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-6 border border-blue-500/20">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-400" />
              Turnkey Service Model
            </h3>
            <div className="space-y-3">
              {[
                { title: "Site Assessment", desc: "Water quality analysis, demand profiling, and custom system design" },
                { title: "Equipment & Installation", desc: "Full mechanical, plumbing, and electrical installation" },
                { title: "DI Tank Exchange", desc: "Scheduled delivery of regenerated tanks — zero downtime" },
                { title: "Monitoring & Maintenance", desc: "24/7 IoT remote monitoring, filter changes, sanitization" },
                { title: "Compliance & Reporting", desc: "AAMI ST108 testing, EPA documentation, audit-ready records" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-400 text-xs font-bold">{i + 1}</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{item.title}</p>
                    <p className="text-slate-400 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    ),

    // SLIDE 4: Medical Device & First Responder - Enhanced with Images
    () => (
      <div className="p-8" data-testid="slide-medical">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
            <Heart className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Medical Device & First Responder Solutions</h2>
            <p className="text-slate-400 text-sm">Licensed DME Supplier + Emergency First Aid + OSHA Compliance</p>
          </div>
        </div>

        {/* Hero Image Banner */}
        <div className="relative rounded-xl overflow-hidden mb-6 h-44">
          <img src={EMT_IMG} alt="Emergency Medical Response" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-red-900/40 to-transparent flex items-center">
            <div className="p-6 max-w-2xl">
              <p className="text-red-400 text-sm font-semibold mb-1">LICENSED MEDICAL DEVICE DISTRIBUTOR</p>
              <p className="text-white text-lg font-bold">Complete First Responder & Medical Supply Chain for Port Facilities</p>
              <p className="text-slate-300 text-sm mt-1">From AED units and trauma kits to ongoing OSHA-compliant restocking — we ensure your first aid stations are always audit-ready and fully stocked for the hazards your workers face daily.</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {/* Emergency First Aid */}
          <div className="bg-slate-800/50 rounded-xl border border-red-500/20 overflow-hidden">
            <img src={AED_IMG} alt="AED Defibrillator Station" className="w-full h-36 object-cover" />
            <div className="p-4">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                Emergency First Aid
              </h4>
              <ul className="space-y-1.5 text-sm text-slate-400">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-red-400 mt-0.5" />AED units, pads & training</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-red-400 mt-0.5" />OSHA-compliant trauma kits</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-red-400 mt-0.5" />Burn treatment & eye wash</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-red-400 mt-0.5" />Spill containment kits</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-red-400 mt-0.5" />Oxygen & airway management</li>
              </ul>
            </div>
          </div>

          {/* On-Site Medical Supply */}
          <div className="bg-slate-800/50 rounded-xl border border-amber-500/20 overflow-hidden">
            <img src={FIRST_AID_IMG} alt="First Aid Kit Medical Supplies" className="w-full h-36 object-cover" />
            <div className="p-4">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Stethoscope className="w-4 h-4 text-amber-400" />
                On-Site Medical Supply
              </h4>
              <ul className="space-y-1.5 text-sm text-slate-400">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-amber-400 mt-0.5" />PPE (gloves, masks, gowns)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-amber-400 mt-0.5" />Wound care & bandaging</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-amber-400 mt-0.5" />Diagnostic instruments</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-amber-400 mt-0.5" />Infection prevention supplies</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-amber-400 mt-0.5" />Stretchers & patient transport</li>
              </ul>
            </div>
          </div>

          {/* Managed Restocking */}
          <div className="bg-slate-800/50 rounded-xl border border-violet-500/20 overflow-hidden">
            <div className="w-full h-36 bg-gradient-to-br from-violet-900/50 to-slate-800 flex items-center justify-center">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-violet-400 mx-auto mb-2" />
                <p className="text-violet-300 text-sm font-semibold">Automated Service</p>
              </div>
            </div>
            <div className="p-4">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-violet-400" />
                Managed Restocking
              </h4>
              <ul className="space-y-1.5 text-sm text-slate-400">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-violet-400 mt-0.5" />Automated reorder triggers</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-violet-400 mt-0.5" />Expiration date tracking</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-violet-400 mt-0.5" />OSHA compliance audits</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-violet-400 mt-0.5" />Monthly station inspections</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-violet-400 mt-0.5" />Emergency surge inventory</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-900/20 to-amber-900/20 rounded-xl p-5 border border-red-500/20">
          <p className="text-white font-semibold mb-2 flex items-center gap-2">
            <Flame className="w-5 h-5 text-red-400" />
            Port-Specific Hazard Readiness
          </p>
          <p className="text-slate-300 text-sm">
            Port environments present unique hazards: chemical exposure, heat stress, heavy machinery injuries, and maritime-specific incidents.
            Our supply catalog and restocking service are tailored to these risks — from heat stroke treatment kits to chemical burn response stations.
            Every station is inspected monthly and documented through AskIsaac for complete audit readiness.
          </p>
        </div>
      </div>
    ),

    // SLIDE 5: AskIsaac Platform - Project Management & GC
    () => (
      <div className="p-8" data-testid="slide-askisaac">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
            <Brain className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">AskIsaac.io Intelligence Platform</h2>
            <p className="text-slate-400 text-sm">Project Management + General Contracting + Compliance Execution</p>
          </div>
        </div>
        <p className="text-slate-300 mb-6 max-w-3xl">
          AskIsaac is our proprietary technology platform that transforms how port facilities manage projects, contractors, and compliance.
          From electrical and plumbing to HVAC and industrial maintenance — every service call, quote, and payment flows through one transparent, auditable system.
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-slate-800/50 rounded-xl p-6 border border-violet-500/20">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5 text-violet-400" />
              Project Management Command Center
            </h3>
            <div className="space-y-3">
              <FeatureRow icon={ClipboardCheck} title="Project Intake & Tracking" desc="From service request to completion — every step documented with photos, GPS, and timestamps." color="text-violet-400" />
              <FeatureRow icon={Workflow} title="Phase Management" desc="Break complex projects into phases with milestones, budgets, and approval gates." color="text-violet-400" />
              <FeatureRow icon={CircleDollarSign} title="Project Accounting" desc="Real-time budget tracking, PO management, invoice reconciliation, and spend analytics." color="text-violet-400" />
              <FeatureRow icon={BarChart3} title="Executive Dashboards" desc="Progress reports, spend-vs-budget, contractor performance, and compliance scoring." color="text-violet-400" />
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-6 border border-cyan-500/20">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <HardHat className="w-5 h-5 text-cyan-400" />
              General Contracting Services
            </h3>
            <div className="space-y-3">
              <FeatureRow icon={Zap} title="Electrical" desc="Panel upgrades, lighting, generator maintenance, emergency circuits for port operations." color="text-cyan-400" />
              <FeatureRow icon={Pipette} title="Plumbing" desc="Industrial plumbing, backflow prevention, pipe repair, water system maintenance." color="text-cyan-400" />
              <FeatureRow icon={Thermometer} title="HVAC" desc="Climate control for terminals, warehouses, server rooms, and office buildings." color="text-cyan-400" />
              <FeatureRow icon={Wrench} title="General Maintenance" desc="Facility repairs, dock maintenance, painting, carpentry, and emergency services." color="text-cyan-400" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-violet-900/30 to-cyan-900/30 rounded-xl p-5 border border-violet-500/20">
          <h3 className="text-white font-semibold mb-3">Compliance Execution & Transparency</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-3">
              <Lock className="w-8 h-8 text-violet-400 mx-auto mb-2" />
              <p className="text-white text-sm font-semibold">Fortress Compliance</p>
              <p className="text-slate-400 text-xs mt-1">Every contractor screened against 82,000+ exclusion records. COI & license tracking.</p>
            </div>
            <div className="text-center p-3">
              <Calculator className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
              <p className="text-white text-sm font-semibold">3-Quote Automation</p>
              <p className="text-slate-400 text-xs mt-1">Blind competitive bidding with auto-generated audit trails for every procurement.</p>
            </div>
            <div className="text-center p-3">
              <Eye className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-white text-sm font-semibold">Full Transparency</p>
              <p className="text-slate-400 text-xs mt-1">Real-time dashboards, GPS verification, photo documentation, digital chain of custody.</p>
            </div>
          </div>
        </div>
      </div>
    ),

    // SLIDE 6: Why Sterile Mate + Contact
    () => (
      <div className="p-8" data-testid="slide-contact">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
            <Award className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Why Sterile Mate for Port of Houston</h2>
            <p className="text-slate-400 text-sm">One Partner. Complete Coverage.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-5 border border-amber-500/20">
              <h3 className="text-amber-400 font-semibold mb-3">Single-Source Advantage</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5" />One contract covers janitorial, water, medical, and maintenance</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5" />Unified billing, reporting, and compliance under one platform</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5" />Reduced vendor management overhead</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5" />Faster response times — one call, multiple service lines</li>
              </ul>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-5 border border-emerald-500/20">
              <h3 className="text-emerald-400 font-semibold mb-3">Houston-Based. Port-Ready.</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" />Local warehouse and emergency inventory</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" />2-hour STAT response for emergencies</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" />Proven track record with Harris Health System</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" />Licensed medical device distributor</li>
              </ul>
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#003366] to-[#001a33] rounded-xl p-6 border border-blue-500/30 flex flex-col justify-between">
            <div>
              <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-12 mb-6" />
              <h3 className="text-white text-xl font-bold mb-2">Let's Build a Resilient Port Together</h3>
              <p className="text-blue-200 text-sm mb-6">
                Schedule a capabilities briefing to see how our 5D Work Engine and AskIsaac platform
                can transform your facility operations, compliance posture, and emergency readiness.
              </p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-white">
                <Phone className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="font-semibold">(800) 988-3892</p>
                  <p className="text-blue-300 text-xs">Direct Line</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-white">
                <Mail className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="font-semibold">orders@sterilemate.com</p>
                  <p className="text-blue-300 text-xs">Procurement & Services</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-white">
                <Globe className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="font-semibold">sterilemate.com</p>
                  <p className="text-blue-300 text-xs">Platform Demo</p>
                </div>
              </div>
              <Link to="/hospital/register" className="block mt-4">
                <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white" size="lg" data-testid="schedule-briefing-btn">
                  <Play className="w-5 h-5 mr-2" />
                  Schedule Capabilities Briefing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    ),
  ];

  const goTo = (idx) => {
    setCurrentSlide(Math.max(0, Math.min(idx, slides.length - 1)));
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-[#0a1628] to-slate-900" data-testid="port-of-houston-page">
      {/* Top Bar */}
      <div className="bg-slate-900/90 border-b border-slate-700/50 sticky top-0 z-20 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" />
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-7" />
          </Link>
          <div className="hidden md:flex items-center gap-1 bg-slate-800/50 rounded-lg p-1 border border-slate-700/50">
            {["Overview", "Company", "Janitorial", "Water", "Medical", "AskIsaac", "Contact"].map((label, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${i === currentSlide ? "bg-cyan-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-700"}`}
                data-testid={`nav-tab-${label.toLowerCase()}`}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="text-slate-500 text-sm">{currentSlide + 1} / {slides.length}</span>
        </div>
      </div>

      {/* Slide Content */}
      <div ref={containerRef} className="max-w-7xl mx-auto">
        <div className="min-h-[calc(100vh-120px)]">
          {slides[currentSlide]()}
        </div>
        <div className="px-8 pb-8">
          <SlideNav current={currentSlide} total={slides.length} onPrev={() => goTo(currentSlide - 1)} onNext={() => goTo(currentSlide + 1)} />
        </div>
      </div>
    </div>
  );
}
